import React, { useState, useEffect } from 'react';
import { Building2, ArrowLeft, MapPin, Phone, Save, AlertCircle } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usersAPI, handleApiError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useFlashMessage } from '../components/FlashMessage';

const OwnerEditLocationForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    address: '',
    phone: '',
    default_valet_price: 0
  });

  useEffect(() => {
    loadLocationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadLocationData = async () => {
    setIsLoading(true);

    try {
      const locationId = parseInt(id);

      // Fetch location from API endpoint
      const response = await usersAPI.getOwnerLocation(locationId);

      if (response.data.success) {
        const location = response.data.data;
        setFormData({
          name: location.name || '',
          type: location.type || 'both',
          address: location.address || '',
          phone: formatPhoneNumber(location.phone || ''),
          default_valet_price: parseFloat(location.default_valet_price) || 0
        });
      } else {
        showDanger('Lokasyon bulunamadı');
        navigate('/owner/locations');
      }
    } catch (err) {
      console.error('Failed to load location:', err);

      // Handle 403 or 404 errors
      if (err.response?.status === 403) {
        showDanger('Bu lokasyona erişim yetkiniz yok');
      } else if (err.response?.status === 404) {
        showDanger('Lokasyon bulunamadı');
      } else {
        showDanger(handleApiError(err));
      }

      navigate('/owner/locations');
    } finally {
      setIsLoading(false);
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleanValue = value.replace(/\D/g, '');

    // Apply Turkish phone number format: 0XXX XXX XX XX
    if (cleanValue.length === 0) return '';
    if (cleanValue.length <= 1) return cleanValue;
    if (cleanValue.length <= 4) return `${cleanValue.slice(0, 4)}`;
    if (cleanValue.length <= 7) return `${cleanValue.slice(0, 4)} ${cleanValue.slice(4)}`;
    if (cleanValue.length <= 9) return `${cleanValue.slice(0, 4)} ${cleanValue.slice(4, 7)} ${cleanValue.slice(7)}`;
    return `${cleanValue.slice(0, 4)} ${cleanValue.slice(4, 7)} ${cleanValue.slice(7, 9)} ${cleanValue.slice(9, 11)}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const formattedValue = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const dataToSend = {
        address: formData.address,
        phone: formData.phone.replace(/\D/g, ''), // Clean phone number
        default_valet_price: parseFloat(formData.default_valet_price) || 0
      };

      await usersAPI.updateOwnerLocation(id, dataToSend);

      showSuccess(`${formData.name} başarıyla güncellendi!`);

      setTimeout(() => {
        navigate('/owner/locations');
      }, 2000);

    } catch (err) {
      console.error('Failed to update location:', err);
      showDanger(handleApiError(err));
    } finally {
      setIsSaving(false);
    }
  };

  const getLocationTypeText = (type) => {
    switch (type) {
      case 'both': return 'Vale + Oto Yıkama';
      case 'wash': return 'Oto Yıkama';
      case 'valet': return 'Vale';
      default: return type;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <FlashContainer />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/owner/locations"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Building2 className="w-7 h-7 mr-3 text-[#7a2c2c]" />
              Lokasyon Düzenle
            </h1>
            <p className="text-gray-600">Lokasyon bilgilerinizi güncelleyin</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Temel Bilgiler</h3>
          </div>
          <div className="card-body space-y-6">
            {/* Read-only fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">
                  Lokasyon Adı
                </label>
                <input
                  type="text"
                  value={formData.name}
                  className="form-input bg-gray-50 cursor-not-allowed"
                  readOnly
                  disabled
                />
                <p className="text-xs text-gray-500 mt-1">Bu alan düzenlenemez</p>
              </div>
              <div>
                <label className="form-label">
                  Lokasyon Tipi
                </label>
                <input
                  type="text"
                  value={getLocationTypeText(formData.type)}
                  className="form-input bg-gray-50 cursor-not-allowed"
                  readOnly
                  disabled
                />
                <p className="text-xs text-gray-500 mt-1">Bu alan düzenlenemez</p>
              </div>
            </div>

            {/* Editable fields */}
            <div>
              <label className="form-label">
                <MapPin className="w-4 h-4 inline mr-2" />
                Adres *
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="form-input"
                rows={3}
                placeholder="Tam adres bilgisini giriniz"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Telefon *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="0212 123 45 67"
                  maxLength="14"
                  required
                />
              </div>
              <div>
                <label className="form-label">
                  Default Vale Fiyatı (₺) *
                </label>
                <input
                  type="number"
                  name="default_valet_price"
                  value={formData.default_valet_price}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Lokasyon için varsayılan vale hizmeti fiyatı</p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1">Bilgilendirme</p>
                  <p>Sadece adres, telefon ve vale fiyatı bilgilerini düzenleyebilirsiniz. Diğer ayarlar için sistem yöneticisiyle iletişime geçin.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4">
          <Link
            to="/owner/locations"
            className="btn-secondary w-full sm:w-auto text-center"
          >
            İptal
          </Link>
          <button
            type="submit"
            disabled={isSaving}
            className="btn-primary w-full sm:w-auto flex items-center justify-center"
          >
            {isSaving ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Değişiklikleri Kaydet
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OwnerEditLocationForm;
